import { useCallback, useEffect, useState } from "react";
import API from "../../lib/api";
import { fetchBlobUrl } from "../../lib/fileOpen";
import { Waveform, Wrench, ListChecks, Play } from "@phosphor-icons/react";
import { toast } from "sonner";

function AudioClip({ url }) {
  const [src, setSrc] = useState("");
  const play = async () => {
    try { setSrc(await fetchBlobUrl(url)); } catch { toast.error("Couldn't load the recording"); }
  };
  if (src) return <audio controls autoPlay src={src} className="h-8 w-full max-w-xs" data-testid="voice-audio" />;
  return <button type="button" onClick={play} className="text-xs text-primary inline-flex items-center gap-1" data-testid="voice-audio-load"><Play className="h-3.5 w-3.5" weight="fill" /> Play recording</button>;
}

/** Staff view of the engineer's site report: notes, parts, follow-ups and the voice notes behind them. */
export default function SiteReportCard({ entityType, entityId, canEdit = true }) {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await API.get(`/site-report/${entityType}/${entityId}`); setD(data); } catch { setD(null); }
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const empty = !d.operative_notes.length && !d.parts_used.length && !d.follow_ups.length && !d.voice_notes.length && !d.completion_notes;
  if (empty) return null;

  const toggle = async (id) => {
    try { const { data } = await API.post(`/site-report/${entityType}/${entityId}/follow-ups/${id}/toggle`); setD(x => ({ ...x, follow_ups: data.follow_ups })); }
    catch { toast.error("Couldn't update"); }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4" data-testid="site-report-card">
      <div>
        <h3 className="text-base font-semibold flex items-center gap-2"><Waveform className="h-4 w-4" /> Engineer's site report</h3>
        {d.on_site_minutes != null && <p className="text-xs text-muted-foreground">{d.on_site_minutes} min on site</p>}
      </div>
      {d.completion_notes && <p className="text-sm whitespace-pre-wrap rounded-md bg-muted/40 p-3" data-testid="site-report-completion">{d.completion_notes}</p>}
      {d.operative_notes.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Site notes</p>
          <ul className="space-y-1.5 text-sm">
            {d.operative_notes.map(n => <li key={n.id} className="rounded-md border border-border p-2.5 whitespace-pre-wrap" data-testid={`site-note-${n.id}`}>{n.text}<span className="block text-[10px] text-muted-foreground mt-1">{n.by_name} · {new Date(n.at).toLocaleString("en-GB")}</span></li>)}
          </ul>
        </div>
      )}
      {d.parts_used.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><Wrench className="h-3 w-3" /> Parts used</p>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-border">
              {d.parts_used.map(p => <tr key={p.id} data-testid={`site-part-${p.id}`}><td className="py-1.5 pr-2">{p.name}{p.notes ? <span className="text-muted-foreground"> — {p.notes}</span> : null}</td><td className="py-1.5 text-right tabular-nums text-muted-foreground whitespace-nowrap">{p.qty} {p.unit}</td></tr>)}
            </tbody>
          </table>
        </div>
      )}
      {d.follow_ups.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><ListChecks className="h-3 w-3" /> Follow-ups</p>
          <ul className="space-y-1 text-sm">
            {d.follow_ups.map(f => (
              <li key={f.id} className="flex items-start gap-2">
                <input type="checkbox" checked={!!f.done} disabled={!canEdit} onChange={() => toggle(f.id)} className="mt-1" data-testid={`site-follow-${f.id}`} />
                <span className={f.done ? "line-through text-muted-foreground" : ""}>{f.text}{f.done && f.done_by ? <span className="text-[10px] text-muted-foreground"> · {f.done_by}</span> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {d.voice_notes.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">Voice recordings ({d.voice_notes.length})</summary>
          <ul className="mt-2 space-y-2">
            {d.voice_notes.map(v => (
              <li key={v.id} className="rounded-md border border-border p-2.5 space-y-1" data-testid={`voice-note-${v.id}`}>
                <p className="text-muted-foreground">{v.operative_name} · {new Date(v.created_at).toLocaleString("en-GB")}{v.duration_seconds ? ` · ${v.duration_seconds}s` : ""}{v.applied_at ? "" : " · not added"}</p>
                <p className="whitespace-pre-wrap text-slate-700 dark:text-slate-300">{v.transcript}</p>
                <AudioClip url={v.audio_url} />
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
